import React, { useState } from 'react'
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tooltip,
  Upload,
  message,
  theme
} from 'antd'
import { hexToRGBA } from '../../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faFilePdf, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { InboxOutlined } from '@ant-design/icons'
import CustomRow from '../../../reusable-components/CustomRow'
import dayjs from 'dayjs'

const { Dragger } = Upload

// Placeholder for API integration - replace with actual API calls
const saveDiagnosticTest = async (patientRef, test) => {
  // In a real implementation, this would call your backend API

  return { ...test, id: Math.random().toString(36).substring(7) }
}

const updateDiagnosticTest = async (patientRef, testId, test) => {
  // In a real implementation, this would call your backend API

  return { ...test }
}

const deleteDiagnosticTest = async (patientRef, testId) => {
  // In a real implementation, this would call your backend API

  return true
}

const DiagnosticsManagement = ({ diagnosticTests = [], setDiagnosticTests, patientRef }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTest, setEditingTest] = useState(null)
  // eslint-disable-next-line
  const [viewingResults, setViewingResults] = useState(null)
  const [fileList, setFileList] = useState([])
  const [form] = Form.useForm()
  const { token } = theme.useToken()

  const handleAddTest = () => {
    setEditingTest(null)
    setFileList([])
    form.resetFields()
    form.setFieldsValue({
      scheduledDate: dayjs(),
      completed: false
    })
    setIsModalOpen(true)
  }

  const handleEditTest = (record) => {
    setEditingTest(record)
    setFileList(record.results ? [{ uid: '-1', name: 'Current Results', status: 'done' }] : [])
    form.setFieldsValue({
      testName: record.testName,
      testType: record.testType,
      scheduledDate: record.scheduledDate ? dayjs(record.scheduledDate) : null,
      completed: record.completed,
      notes: record.notes
    })
    setIsModalOpen(true)
  }

  const handleDeleteTest = async (record) => {
    try {
      await deleteDiagnosticTest(patientRef, record.id)
      setDiagnosticTests(diagnosticTests.filter((test) => test.id !== record.id))
      message.success('Diagnostic test deleted successfully')
    } catch (error) {
      message.error('Failed to delete diagnostic test')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      // Process file upload if any
      let results = null
      if (fileList.length > 0 && fileList[0].originFileObj) {
        // In a real implementation, this would upload the file to your server
        // and get back a URL or file identifier
        results = {
          fileName: fileList[0].name,
          fileUrl: 'https://example.com/results.pdf',
          uploadDate: new Date().toISOString()
        }
      } else if (editingTest && editingTest.results) {
        // Keep existing results if no new file uploaded
        results = editingTest.results
      }

      if (editingTest) {
        // Update existing test
        const updatedTest = await updateDiagnosticTest(patientRef, editingTest.id, {
          ...editingTest,
          ...values,
          scheduledDate: values.scheduledDate ? values.scheduledDate.format('YYYY-MM-DD') : null,
          results
        })
        setDiagnosticTests(diagnosticTests.map((test) => (test.id === editingTest.id ? updatedTest : test)))
        message.success('Diagnostic test updated successfully')
      } else {
        // Add new test
        const newTest = await saveDiagnosticTest(patientRef, {
          ...values,
          scheduledDate: values.scheduledDate ? values.scheduledDate.format('YYYY-MM-DD') : null,
          results
        })
        setDiagnosticTests([...diagnosticTests, newTest])
        message.success('Diagnostic test added successfully')
      }
      setIsModalOpen(false)
    } catch (error) {
      // Form validation error or API error
      console.error('Failed to save diagnostic test:', error)
    }
  }

  const handleViewResults = (record) => {
    if (record.results) {
      setViewingResults(record)
      // In a real implementation, this would open a PDF viewer or navigate to the file URL
      window.open(record.results.fileUrl, '_blank')
    }
  }

  const columns = [
    {
      title: 'Test Name',
      dataIndex: 'testName',
      key: 'testName'
    },
    {
      title: 'Type',
      dataIndex: 'testType',
      key: 'testType'
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD') : 'Not scheduled')
    },
    {
      title: 'Status',
      dataIndex: 'completed',
      key: 'completed',
      render: (completed) => (
        <span style={{ color: completed ? token.colorSuccess : token.colorWarning }}>
          {completed ? 'Completed' : 'Pending'}
        </span>
      )
    },
    {
      title: 'Results',
      key: 'results',
      render: (_, record) =>
        record.results ? (
          <Button type='link' icon={<FontAwesomeIcon icon={faFilePdf} />} onClick={() => handleViewResults(record)}>
            View
          </Button>
        ) : (
          'No results'
        )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title='Edit'>
            <Button type='text' icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => handleEditTest(record)} />
          </Tooltip>
          <Tooltip title='Delete'>
            <Button
              type='text'
              danger
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => handleDeleteTest(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  const testTypes = [
    'Blood Test',
    'Urine Test',
    'Imaging',
    'X-Ray',
    'CT Scan',
    'MRI',
    'Ultrasound',
    'Biopsy',
    'Endoscopy',
    'ECG/EKG',
    'Other'
  ]

  return (
    <>
      <CustomRow>
        <Col span={24}>
          <Card
            title='Diagnostic Tests Management'
            headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
            extra={
              <Button type='primary' icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAddTest}>
                Add Test
              </Button>
            }
          >
            <Table dataSource={diagnosticTests} columns={columns} rowKey='id' />
          </Card>
        </Col>
      </CustomRow>

      <Modal
        title={editingTest ? 'Edit Diagnostic Test' : 'Add Diagnostic Test'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='testName'
            label='Test Name'
            rules={[{ required: true, message: 'Please enter the test name' }]}
          >
            <Input placeholder='e.g., Complete Blood Count (CBC), Liver Function Test' />
          </Form.Item>

          <Form.Item
            name='testType'
            label='Test Type'
            rules={[{ required: true, message: 'Please select the test type' }]}
          >
            <Input.Group compact>
              <Input style={{ width: '100%' }} placeholder='Select or type a test type' list='testTypeOptions' />
              <datalist id='testTypeOptions'>
                {testTypes.map((type) => (
                  <option key={type} value={type} />
                ))}
              </datalist>
            </Input.Group>
          </Form.Item>

          <Form.Item name='scheduledDate' label='Scheduled Date'>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='completed' valuePropName='checked'>
            <Checkbox>Test Completed</Checkbox>
          </Form.Item>

          <Form.Item name='notes' label='Notes'>
            <Input.TextArea rows={2} placeholder='Additional notes about the test' />
          </Form.Item>

          <Form.Item label='Test Results' name='results'>
            <Dragger
              fileList={fileList}
              onChange={(info) => {
                setFileList(info.fileList.slice(-1))
              }}
              beforeUpload={() => false}
              maxCount={1}
            >
              <p className='ant-upload-drag-icon'>
                <InboxOutlined />
              </p>
              <p className='ant-upload-text'>Click or drag file to upload test results</p>
              <p className='ant-upload-hint'>Support for PDF files only</p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default DiagnosticsManagement
